import React, { useCallback, useState, useMemo } from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useUrlBuilder } from '@bingads-webui-cc-react/url-builder-react';
import { FilePickerWithProgress } from '@bingads-webui-react/file-picker-with-progress';
import { InputFormGroup, LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { ReverseChargeDocumentFileAllowedExtension, TaxFormMode, ReverseChargeDocumentMaxSizeLimitation, ApiFileUrlBuilderName, FileScenarioMapping, ReverseChargeDocumentTemplateFileGuid } from '../constants';
import { getTaxFormsStyles } from '../styles';
import { TaxCertificationShape } from '../shapes';
import { useFilePickerProgressI18n } from '../hooks/use-i18n';

export const ReverseChargeDocumentFilePicker = ({
  i18n,
  documents,
  onDocumentsChange,
  mtTaxCertification,
  taxFormMode,
  showLineByLineFormGroup,
  alwaysShowError,
  disabled,
  required,
  urlBuilder,
}) => {
  const filePickerProgressI18n = useFilePickerProgressI18n(i18n);
  const CustomizedFormGroup = showLineByLineFormGroup ? LineByLineFormGroup : InputFormGroup;
  const isEditMode = taxFormMode === TaxFormMode.EditMode;
  const reverseChargeDocumentsObj = mtTaxCertification.reverseChargeDocuments[0];
  const documentGuid = _.get(reverseChargeDocumentsObj, 'guid');
  const additionalFormData = useMemo(() => ({ scenario: FileScenarioMapping.ReverseChargeDocument }), []);
  const reverseChargeDocumentTemplateFormData = useMemo(() => ({ scenario: FileScenarioMapping.ReverseChargeDocumentTemplate }), []);

  const classes = useDefaultStyles(getTaxFormsStyles);

  const [reverseDocumentsFileState, setReverseDocumentsFileState] = useState({
    fileList: documents || [],
    isValid: false,
  });

  const initialFilesUploadCompletedInfo = useMemo(() => _.reduce(documents, (prev, curr) => {
    if (curr && curr.id && curr.guid) {
      // eslint-disable-next-line no-param-reassign
      prev[curr.id] = {
        FileGuid: curr.guid,
        success: true,
      };
    }
    return prev;
  }, {}), [documents]);

  const onFileChange = useCallback(({ fileList, isValid }) => {
    setReverseDocumentsFileState({
      fileList,
      isValid,
    });

    onDocumentsChange({
      documents: fileList,
      isValid,
    });
  }, [onDocumentsChange]);

  const fileValidator = useCallback(file => _.result(file, 'size', null) > ReverseChargeDocumentMaxSizeLimitation && i18n.getString(_TL_('The file you’ve uploaded is too large. Please reduce the file size to less than 20 megabytes and try again.')), [i18n]);

  const onFileCompletedInfoChange = useCallback((reverseDoucmentFileCompletedInfo) => {
    const documentsWithGuid = reverseDocumentsFileState.fileList.map(file => ((reverseDoucmentFileCompletedInfo[file.id] && reverseDoucmentFileCompletedInfo[file.id].success)
      ? { ...file, guid: reverseDoucmentFileCompletedInfo[file.id].FileGuid }
      : { ...file, guid: null }
    ));
    onDocumentsChange({
      documents: documentsWithGuid,
      isValid: reverseDocumentsFileState.isValid,
    });
  }, [onDocumentsChange, reverseDocumentsFileState]);

  const getApiFileUrlBuilderFromProp = useCallback((urlBuilderProp, config) => {
    const UrlBuilders = _.get(urlBuilderProp, 'UrlBuilders');
    return UrlBuilders && _.isFunction(UrlBuilders[ApiFileUrlBuilderName]) ? new UrlBuilders[ApiFileUrlBuilderName](config) : {};
  }, []);

  let apiFileUrlBuilder = useUrlBuilder(ApiFileUrlBuilderName);
  apiFileUrlBuilder = !_.isEmpty(apiFileUrlBuilder) ? apiFileUrlBuilder : getApiFileUrlBuilderFromProp(urlBuilder);
  const downloadFileUrl = useCallback(({ FileGuid }) => apiFileUrlBuilder.params({ FileGuid, ...reverseChargeDocumentTemplateFormData }).url, [reverseChargeDocumentTemplateFormData, apiFileUrlBuilder]);

  const templateloadLink = useMemo(() => {
    const templateDownloadLink = !_.isEmpty(apiFileUrlBuilder)
      && downloadFileUrl({ FileGuid: ReverseChargeDocumentTemplateFileGuid });

    return (
      <span className={classNames('template-download-span', classes.downloadCertLink)}>
        <ReactTemplate
          model={{
            link: text => (templateDownloadLink &&
              <a
                className={classNames('template-download-link', classes.downloadCertLink)}
                href={templateDownloadLink}
              >
                {text}
              </a>),
          }}
          template={i18n.getString(_TL_('(Please download {{link}}this document{{/link}}, fill in the required fields, and upload it below.)'))}
        />
      </span>
    );
  }, [apiFileUrlBuilder, classes.downloadCertLink, downloadFileUrl, i18n]);

  const showSuccessStatusText = !_.isEmpty(reverseDocumentsFileState.fileList) || documentGuid;

  return (
    <CustomizedFormGroup
      isStatic
      label={i18n.getString(_TL_('Reverse Charge Document'))}
      className="reverse-document-file-picker-form-group"
      labelColExtraClass={classes.taxLabelColClass}
      labelClass={classes.taxCertificateLabel}
      customLabelSuffix={isEditMode && templateloadLink}
    >
      <div className="reverse-charge-document-status">
        {showSuccessStatusText ? i18n.getString(_TL_('Your reverse charge document has been uploaded.')) : (!isEditMode && i18n.getString(_TL_('(Not set)')))}
      </div>
      {isEditMode && (
        <FilePickerWithProgress
          onFileCompletedInfoChange={onFileCompletedInfoChange}
          endPoint={apiFileUrlBuilder.url || ''}
          className="reverse-document-file-picker"
          i18n={filePickerProgressI18n}
          fileList={reverseDocumentsFileState.fileList || []}
          initialFilesUploadCompletedInfo={initialFilesUploadCompletedInfo}
          onFileChange={onFileChange}
          allowedFileExtensions={ReverseChargeDocumentFileAllowedExtension}
          validator={fileValidator}
          maxCount={1}
          additionalFormData={additionalFormData}
          alwaysShowError={alwaysShowError}
          disabled={disabled}
          required={required}
        />
      )}
    </CustomizedFormGroup>
  );
};

ReverseChargeDocumentFilePicker.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  documents: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    displayName: PropTypes.string.isRequired,
    file: PropTypes.instanceOf(File),
  })),
  mtTaxCertification: TaxCertificationShape,
  onDocumentsChange: PropTypes.func,
  taxFormMode: PropTypes.string,
  showLineByLineFormGroup: PropTypes.bool,
  alwaysShowError: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  urlBuilder: PropTypes.shape({
    UrlBuilders: PropTypes.shape({}),
    UrlHelper: PropTypes.shape({}),
  }),
};

ReverseChargeDocumentFilePicker.defaultProps = {
  documents: [],
  onDocumentsChange: _.noop,
  mtTaxCertification: {},
  taxFormMode: TaxFormMode.EditMode,
  showLineByLineFormGroup: false,
  alwaysShowError: false,
  disabled: false,
  required: false,
  urlBuilder: {
    UrlBuilders: {},
    UrlHelper: {},
  },
};
